import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleLeft, faAngleRight, faSpinner, faCamera } from '@fortawesome/free-solid-svg-icons';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import PageHeadingSection from '../../../../common/pageheading/PageHeadingSection';
import apiServices from '../../../../../services/apiService';
import CustomSelect from '../../../../common/formfields/CustomSelect';
import { primaryColor, secondaryColor } from '../../../../../utils/comon';
import storageService from '../../../../../utils/storageService';
import Breadcrumbs from '../../../../common/BreadCrum/BreadCrum';

const UserForm = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [isLoading, setIsLoading] = useState(false);

    const [sites, setSites] = useState([]);
    const [allUnits, setAllUnits] = useState([]);
    const [roles, setRoles] = useState([]);
    const [units, setUnits] = useState([]);
    const [currentUserUnitLabel, setCurrentUserUnitLabel] = useState('');

    const [formData, setFormData] = useState({
        userId: null,
        title: null,
        firstName: '',
        lastName: '',
        mobileNumber: '',
        email: '',
        userAddress: '',
        gender: '',
        dateOfBirth: '',
        employeeBloodGroup: '',
        userImage: '',
        roleId: null,
        userType: 'Resident',
        siteId: null,
        wingId: null,
        floorId: null,
        unitId: null,
        isActive: 'Active',
        country: '',
        city: ''
    });

    const isEdit = !!location.state?.id;
    const editItem = location.state?.item;
    const employeeCode = storageService.getItem('employeeCode') || storageService.getItem('employeeName') || '';
    const loggedInRoleId = Number(storageService.getItem('roleId')) || 1;

    useEffect(() => {
        const fetchMasterData = async () => {
            try {
                // Fetch Current User Details to auto-fill for Site Admin & Unit Admin
                let currentSiteId = null;
                let currentUnitId = null;
                let currentCountry = '';
                let currentCity = '';
                
                if (employeeCode) {
                    const userRes = await apiServices({
                        url: '/ResidentialUser/GetResidentialUserById',
                        method: 'POST',
                        data: { userId: employeeCode }
                    });
                    if (userRes.success && userRes.data?.data?.[0]) {
                        const u = userRes.data.data[0];
                        currentSiteId = u.siteId;
                        currentUnitId = u.unitId;
                        currentCountry = u.country || '';
                        currentCity = u.city || '';
                        setCurrentUserUnitLabel(`${u.unitName || ''}(${u.unitNumber || ''})-${u.wingName || ''}`);
                        
                        if (!isEdit && (loggedInRoleId === 3 || loggedInRoleId === 4)) {
                            setFormData(prev => ({
                                ...prev,
                                siteId: currentSiteId,
                                country: currentCountry,
                                city: currentCity,
                                ...(loggedInRoleId === 4 ? {
                                    unitId: u.unitId,
                                    wingId: u.wingId,
                                    floorId: u.floorId
                                } : {})
                            }));
                        }
                    }
                }

                // Fetch Sites
                const siteRes = await apiServices({ method: 'GET', url: '/ResidentialSite/GetAllResidentialSites' });
                if (siteRes.success || siteRes.isSuccess || siteRes.IsSuccess || siteRes) {
                    let siteData = Array.isArray(siteRes.data) ? siteRes.data : (siteRes.data?.data || siteRes);
                    if (Array.isArray(siteData)) {
                        let activeSites = siteData.filter(s => s.IsActive === 'Active' || s.isActive === 'Active');
                        
                        // If Site Admin (3), restrict options to just their own site
                        if (loggedInRoleId === 3 && currentSiteId) {
                            activeSites = activeSites.filter(s => (s.SiteId || s.siteId) === currentSiteId);
                        }

                        setSites(activeSites.map(s => ({
                            label: s.SiteName || s.siteName,
                            value: s.SiteId || s.siteId,
                            country: s.CountryName || s.countryName,
                            city: s.CityName || s.cityName
                        })));
                    }
                }

                // Fetch Roles
                const roleRes = await apiServices({ method: 'GET', url: '/ResidentialRoleMaster/GetResidentialRoles' });
                if (roleRes.success || roleRes.isSuccess || roleRes.IsSuccess || roleRes) {
                    let roleData = Array.isArray(roleRes.data) ? roleRes.data : (roleRes.data?.data || roleRes);
                    if (Array.isArray(roleData)) {
                        let allowedRoles = [];
                        if (loggedInRoleId === 1) allowedRoles = [3];
                        else if (loggedInRoleId === 3) allowedRoles = [4, 6, 7];
                        else if (loggedInRoleId === 4) allowedRoles = [5];

                        const filteredRoles = roleData.filter(r => allowedRoles.includes(r.roleId || r.RoleId));
                        setRoles(filteredRoles.map(r => ({
                            label: r.roleName || r.RoleName,
                            value: r.roleId || r.RoleId
                        })));
                    }
                }

                // Fetch Units (Contains wing and floor info)
                const unitRes = await apiServices({ method: 'GET', url: '/ResidentialUnitMaster/GetAllUnitMaster' });
                if (unitRes.success || unitRes.isSuccess || unitRes.IsSuccess || unitRes) {
                    let unitData = Array.isArray(unitRes.data) ? unitRes.data : (unitRes.data?.data || unitRes);
                    if (Array.isArray(unitData)) setAllUnits(unitData);
                }

                if (isEdit && editItem) {
                    setFormData({
                        userId: editItem.userId || editItem.UserId || null,
                        title: editItem.title || editItem.Title || null,
                        firstName: editItem.firstName || editItem.FirstName || '',
                        lastName: editItem.lastName || editItem.LastName || '',
                        mobileNumber: editItem.mobileNumber || editItem.MobileNumber || '',
                        email: editItem.email || editItem.Email || '',
                        userAddress: editItem.userAddress || editItem.UserAddress || '',
                        gender: editItem.gender || editItem.Gender || '',
                        dateOfBirth: editItem.dateOfBirth ? editItem.dateOfBirth.split('T')[0] : '',
                        employeeBloodGroup: editItem.employeeBloodGroup || editItem.EmployeeBloodGroup || '',
                        userImage: editItem.userImage || editItem.UserImage || '',
                        roleId: editItem.roleId || editItem.RoleId || null,
                        userType: editItem.userType || editItem.UserType || 'Resident',
                        siteId: editItem.siteId || editItem.SiteId || null,
                        wingId: editItem.wingId || editItem.WingId || null,
                        floorId: editItem.floorId || editItem.FloorId || null,
                        unitId: editItem.unitId || editItem.UnitId || null,
                        isActive: editItem.isActive || editItem.IsActive || (editItem.isActiveId === 1 ? 'Active' : 'Inactive') || 'Active',
                        country: editItem.country || '',
                        city: editItem.city || ''
                    });
                }
            } catch (error) {
                console.error("Error fetching data:", error);
                toast.error("Failed to load necessary data.");
            }
        };

        fetchMasterData();
    }, [isEdit, editItem, employeeCode, loggedInRoleId]);

    // Cascading logic for Site -> Units
    useEffect(() => {
        const selectedSite = sites.find(s => s.value === formData.siteId);
        if (selectedSite && (!isEdit || !formData.country)) {
            setFormData(prev => ({ ...prev, country: selectedSite.country || '', city: selectedSite.city || '' }));
        }

        if (!formData.siteId) { setUnits([]); return; }
        
        setUnits(allUnits
            .filter(u => (u.siteId || u.SiteId) === formData.siteId)
            .map(u => ({ 
                label: `${u.unitName || u.UnitName || ''}(${u.unitNumber || u.UnitNumber || ''})-${u.wingName || u.WingName || ''}`, 
                value: u.unitId || u.UnitId || u.Id || u.id,
                wingId: u.wingId || u.WingId,
                floorId: u.floorId || u.FloorId
            }))
        );
    }, [formData.siteId, allUnits, sites, isEdit]);

    const handleInputChange = (e) => {
        const { id, value } = e.target;

        // Custom validation for names (only letters)
        if (id === 'firstName' || id === 'lastName') {
            if (value && !/^[A-Za-z]+$/.test(value)) return;
        }

        // Custom validation for mobile (only numbers)
        if (id === 'mobileNumber') {
            if (value && !/^\d+$/.test(value)) return;
            if (value.length > 10) return;
        }

        setFormData(prev => ({ ...prev, [id]: value }));
    };

    const handleSelectChange = (key, option) => {
        setFormData(prev => {
            const newData = { ...prev, [key]: option ? option.value : null };
            
            if (key === 'siteId') { 
                newData.unitId = null; 
                newData.wingId = null; 
                newData.floorId = null; 
            }
            
            if (key === 'unitId') {
                if (option) {
                    newData.wingId = option.wingId;
                    newData.floorId = option.floorId;
                } else {
                    newData.wingId = null;
                    newData.floorId = null;
                }
            }
            
            // Auto mapping userType based on role selection just like angular
            if (key === 'roleId') {
                if (newData.roleId === 4) newData.userType = 'Resident';
                if (newData.roleId === 6) newData.userType = 'Rental';
            }

            return newData;
        });
    };

    const handleImageUpload = (e) => {
        const file = e.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onload = () => {
                setFormData(prev => ({ ...prev, userImage: reader.result }));
            };
            reader.readAsDataURL(file);
        }
    };

    const calculateAge = (dob) => {
        if (!dob) return 0;
        const birthDate = new Date(dob);
        const today = new Date();
        let age = today.getFullYear() - birthDate.getFullYear();
        const monthDiff = today.getMonth() - birthDate.getMonth();
        if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
            age--;
        }
        return age;
    };

    const validateForm = () => {
        if (!formData.title) return toast.warning("Title is required"), false;
        if (!formData.firstName) return toast.warning("First Name is required"), false;
        if (!formData.lastName) return toast.warning("Last Name is required"), false;
        
        if (!formData.mobileNumber) return toast.warning("Mobile Number is required"), false;
        if (formData.mobileNumber.length !== 10) return toast.warning("Mobile Number must be 10 digits"), false;
        
        // Prevent all same numbers in mobile
        const isAllSame = formData.mobileNumber.split('').every(d => d === formData.mobileNumber[0]);
        if (isAllSame && formData.mobileNumber.length >= 8) {
            return toast.warning("Invalid mobile number: same digits are not allowed."), false;
        }

        if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
            return toast.warning("Please enter a valid email address."), false;
        }

        if (!formData.userAddress) return toast.warning("Address is required"), false;
        if (!formData.gender) return toast.warning("Gender is required"), false;
        if (!formData.siteId) return toast.warning("Society is required"), false;
        if (!formData.roleId) return toast.warning("Access Level (Role) is required"), false;
        if (!formData.unitId) return toast.warning("Flat is required"), false;
        return true;
    };

    const handleSubmit = async () => {
        if (!validateForm()) return;
        setIsLoading(true);

        const dobString = formData.dateOfBirth ? `${formData.dateOfBirth}T00:00:00` : '';

        const payload = {
            userId: formData.userId || 0,
            title: formData.title,
            firstName: formData.firstName,
            lastName: formData.lastName,
            age: calculateAge(formData.dateOfBirth),
            mobileNumber: formData.mobileNumber,
            email: formData.email,
            userAddress: formData.userAddress,
            gender: formData.gender,
            dateOfBirth: dobString,
            roleId: formData.roleId,
            userType: formData.userType,
            siteId: formData.siteId,
            wingId: formData.wingId,
            floorId: formData.floorId,
            unitId: formData.unitId,
            employeeBloodGroup: formData.employeeBloodGroup,
            userImage: formData.userImage,
            isTenant: 0,
            country: formData.country,
            city: formData.city,
            createdBy: employeeCode || 'sys',
            isActiveId: formData.isActive === 'Active' ? 1 : 0,
            status: formData.isActive,
            isActive: formData.isActive
        };

        try {
            const url = isEdit ? `/ResidentialUser/UpdateResidentialUser/${payload.userId}` : `/ResidentialUser/AddResidentialUser`;
            const method = isEdit ? 'PUT' : 'POST';

            const response = await apiServices({ method, url, data: payload });

            if (response.success || response.isSuccess || response.IsSuccess || response) {
                toast.success(`User ${isEdit ? 'updated' : 'created'} successfully!`);
                navigate('/zentres/Family-management/Family-list');
            } else {
                toast.error(response.message || `Failed to ${isEdit ? 'update' : 'create'} user.`);
            }
        } catch (error) {
            console.error("Submission error:", error);
            toast.error("An error occurred during submission.");
        } finally {
            setIsLoading(false);
        }
    };

    const todayString = new Date().toISOString().split('T')[0];

    return (
        <div className="bg-white px-6 py-4 border flex flex-col rounded-lg flex-1 overflow-y-auto pt-4 scrollbar-hide">
            <Breadcrumbs />
            <PageHeadingSection
                Navbar={true}
                mainHeading={`${isEdit ? 'Update' : 'Add'} Family Member`}
                subHeading={`Please fill out the form below to ${isEdit ? 'update the existing' : 'create a new'} user.`}
            />

            <div className="mt-6 w-full max-w-5xl">
                <div className="bg-gray-50/50 p-6 rounded-2xl border border-gray-100 flex flex-col gap-6 shadow-sm">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-6">
                        
                        <div className="flex flex-col gap-2">
                            <CustomSelect
                                label={<span className="text-xs font-bold text-gray-700 block uppercase tracking-wider">Title <span className="text-red-500">*</span></span>}
                                options={['Mr.', 'Master', 'Mrs.', 'Miss', 'Ms.', 'Dr.', 'Other'].map(t => ({ label: t, value: t }))}
                                value={formData.title ? { label: formData.title, value: formData.title } : null}
                                onChange={(opt) => handleSelectChange('title', opt)}
                                placeholder="Select Title"
                            />
                        </div>

                        <div className="flex flex-col gap-2">
                            <label className="text-xs font-bold text-gray-700 uppercase tracking-wider block">First Name <span className="text-red-500">*</span></label>
                            <input 
                                className="w-full px-3 py-2 bg-[#F8FAFC] border border-gray-200 rounded-md text-sm font-medium focus:outline-none focus:border-gray-400 transition-all placeholder:text-gray-400" 
                                type="text" 
                                id="firstName" 
                                placeholder="Enter First Name"
                                value={formData.firstName} 
                                onChange={handleInputChange} 
                            />
                        </div>
                        
                        <div className="flex flex-col gap-2">
                            <label className="text-xs font-bold text-gray-700 uppercase tracking-wider block">Last Name <span className="text-red-500">*</span></label>
                            <input 
                                className="w-full px-3 py-2 bg-[#F8FAFC] border border-gray-200 rounded-md text-sm font-medium focus:outline-none focus:border-gray-400 transition-all placeholder:text-gray-400" 
                                type="text" 
                                id="lastName" 
                                placeholder="Enter Last Name"
                                value={formData.lastName} 
                                onChange={handleInputChange} 
                            />
                        </div>

                        <div className="flex flex-col gap-2">
                            <label className="text-xs font-bold text-gray-700 uppercase tracking-wider block">Mobile <span className="text-red-500">*</span></label>
                            <input 
                                className="w-full px-3 py-2 bg-[#F8FAFC] border border-gray-200 rounded-md text-sm font-medium focus:outline-none focus:border-gray-400 transition-all placeholder:text-gray-400" 
                                type="text" 
                                id="mobileNumber" 
                                placeholder="Enter Mobile"
                                maxLength={10}
                                value={formData.mobileNumber} 
                                onChange={handleInputChange} 
                            />
                        </div>

                        <div className="flex flex-col gap-2">
                            <label className="text-xs font-bold text-gray-700 uppercase tracking-wider block">Email</label>
                            <input 
                                className="w-full px-3 py-2 bg-[#F8FAFC] border border-gray-200 rounded-md text-sm font-medium focus:outline-none focus:border-gray-400 transition-all placeholder:text-gray-400" 
                                type="email" 
                                id="email" 
                                placeholder="Enter Email"
                                value={formData.email} 
                                onChange={handleInputChange} 
                            />
                        </div>

                        <div className="flex flex-col gap-2">
                            <label className="text-xs font-bold text-gray-700 uppercase tracking-wider block">Address <span className="text-red-500">*</span></label>
                            <input 
                                className="w-full px-3 py-2 bg-[#F8FAFC] border border-gray-200 rounded-md text-sm font-medium focus:outline-none focus:border-gray-400 transition-all placeholder:text-gray-400" 
                                type="text" 
                                id="userAddress" 
                                placeholder="Enter Address"
                                value={formData.userAddress} 
                                onChange={handleInputChange} 
                            />
                        </div>

                        <div className="flex flex-col gap-2">
                            <CustomSelect
                                label={<span className="text-xs font-bold text-gray-700 block uppercase tracking-wider">Gender <span className="text-red-500">*</span></span>}
                                options={[
                                    { label: 'Male', value: 'Male' }, 
                                    { label: 'Female', value: 'Female' }, 
                                    { label: 'Other', value: 'Other' },
                                    { label: 'Prefer not to say', value: 'Prefer not to say'}
                                ]}
                                value={formData.gender ? { label: formData.gender, value: formData.gender } : null}
                                onChange={(opt) => handleSelectChange('gender', opt)}
                                placeholder="Select Gender"
                            />
                        </div>

                        <div className="flex flex-col gap-2">
                            <label className="text-xs font-bold text-gray-700 uppercase tracking-wider block">Date of Birth</label>
                            <input 
                                className="w-full px-3 py-2 bg-[#F8FAFC] border border-gray-200 rounded-md text-sm font-medium focus:outline-none focus:border-gray-400 transition-all placeholder:text-gray-400" 
                                type="date" 
                                id="dateOfBirth" 
                                max={todayString}
                                value={formData.dateOfBirth} 
                                onChange={handleInputChange} 
                            />
                        </div>
                        
                        <div className="flex flex-col gap-2">
                            <CustomSelect
                                label={<span className="text-xs font-bold text-gray-700 block uppercase tracking-wider">Access Level <span className="text-red-500">*</span></span>}
                                options={roles}
                                value={roles.find(r => r.value === formData.roleId) || null}
                                onChange={(opt) => handleSelectChange('roleId', opt)}
                                placeholder="Select Access Level"
                            />
                        </div>

                        {/* Hide Society Dropdown completely for Unit Admins (Role 4) */}
                        {(loggedInRoleId === 1 || loggedInRoleId === 3) && (
                            <div className="flex flex-col gap-2">
                                <CustomSelect
                                    label={<span className="text-xs font-bold text-gray-700 block uppercase tracking-wider">Society <span className="text-red-500">*</span></span>}
                                    options={sites}
                                    value={sites.find(s => s.value === formData.siteId) || null}
                                    onChange={(opt) => handleSelectChange('siteId', opt)}
                                    placeholder="Select Society"
                                />
                            </div>
                        )}

                        {/* Flat Dropdown for Super Admin & Site Admin */}
                        {(loggedInRoleId === 1 || loggedInRoleId === 3) && (
                            <div className="flex flex-col gap-2">
                                <CustomSelect
                                    label={<span className="text-xs font-bold text-gray-700 block uppercase tracking-wider">Flat <span className="text-red-500">*</span></span>}
                                    options={units}
                                    value={units.find(u => u.value === formData.unitId) || null}
                                    onChange={(opt) => handleSelectChange('unitId', opt)}
                                    disabled={!formData.siteId}
                                    placeholder="Select Flat"
                                />
                            </div>
                        )}

                        {/* Flat Read-Only Input for Unit Admin (Role 4) */}
                        {loggedInRoleId === 4 && (
                            <div className="flex flex-col gap-2">
                                <label className="text-xs font-bold text-gray-700 uppercase tracking-wider block">Flat <span className="text-red-500">*</span></label>
                                <input 
                                    className="w-full px-3 py-2 bg-[#E2E8F0] border border-gray-300 rounded-md text-sm font-bold text-gray-600 focus:outline-none" 
                                    type="text" 
                                    value={currentUserUnitLabel} 
                                    readOnly 
                                />
                            </div>
                        )}

                        {isEdit && (
                            <div className="flex flex-col gap-2">
                                <CustomSelect
                                    label={<span className="text-xs font-bold text-gray-700 block uppercase tracking-wider">Status <span className="text-red-500">*</span></span>}
                                    options={[{ label: 'Active', value: 'Active' }, { label: 'Inactive', value: 'Inactive' }]}
                                    value={formData.isActive ? { label: formData.isActive, value: formData.isActive } : null}
                                    onChange={(opt) => handleSelectChange('isActive', opt)}
                                    placeholder="Select Status"
                                />
                            </div>
                        )}
                        
                        <div className="flex flex-col gap-2">
                            <CustomSelect
                                label={<span className="text-xs font-bold text-gray-700 block uppercase tracking-wider">Blood Group</span>}
                                options={['A+', 'A-', 'B+', 'B-', 'O+', 'O-', 'AB+', 'AB-'].map(bg => ({ label: bg, value: bg }))}
                                value={formData.employeeBloodGroup ? { label: formData.employeeBloodGroup, value: formData.employeeBloodGroup } : null}
                                onChange={(opt) => handleSelectChange('employeeBloodGroup', opt)}
                                placeholder="Select Blood Group"
                            />
                        </div>

                        {/* Image Upload Area */}
                        <div className="flex flex-col gap-2 md:col-span-2">
                            <label className="text-xs font-bold text-gray-700 uppercase tracking-wider block">User Image</label>
                            <div className="relative group cursor-pointer w-48 h-48 rounded-xl border-2 border-dashed border-gray-300 hover:border-blue-500 transition-colors bg-white flex items-center justify-center overflow-hidden">
                                {formData.userImage ? (
                                    <img src={formData.userImage} alt="Preview" className="w-full h-full object-cover p-2" />
                                ) : (
                                    <div className="flex flex-col items-center justify-center text-gray-400 group-hover:text-blue-500 transition-colors">
                                        <FontAwesomeIcon icon={faCamera} className="text-3xl mb-2" />
                                        <span className="text-[10px] font-bold text-center px-4 uppercase tracking-wide">Upload Photo</span>
                                    </div>
                                )}
                                <input
                                    type="file"
                                    accept="image/*"
                                    onChange={handleImageUpload}
                                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                                />
                            </div>
                        </div>

                    </div>
                </div>

                {/* Footer Actions */}
                <div className="flex justify-between items-center mt-6 pt-6 mb-8">
                    <button
                        type="button"
                        onClick={() => navigate("/zentres/Family-management/Family-list")}
                        className="px-6 py-2.5 border border-gray-200 rounded-xl text-xs font-bold flex items-center gap-2 text-gray-600 hover:bg-gray-50 active:scale-95 transition-all shadow-sm"
                    >
                        <FontAwesomeIcon icon={faAngleLeft} /> Cancel
                    </button>

                    <button
                        onClick={handleSubmit}
                        disabled={isLoading}
                        className="px-8 py-2.5 rounded-xl text-xs font-bold flex items-center gap-2 shadow-sm hover:shadow-md active:scale-95 transition-all duration-200 disabled:opacity-50"
                        style={{ backgroundColor: secondaryColor(), color: primaryColor() }}
                        onMouseEnter={(e) => {
                            if (!isLoading) {
                                e.currentTarget.style.backgroundColor = primaryColor();
                                e.currentTarget.style.color = '#FFFFFF';
                            }
                        }}
                        onMouseLeave={(e) => {
                            if (!isLoading) {
                                e.currentTarget.style.backgroundColor = secondaryColor();
                                e.currentTarget.style.color = primaryColor();
                            }
                        }}
                    >
                        {isLoading ? <FontAwesomeIcon icon={faSpinner} className="animate-spin" /> : (isEdit ? 'Update User' : 'Submit User')}
                        <FontAwesomeIcon icon={faAngleRight} />
                    </button>
                </div>
            </div>
        </div>
    );
};

export default UserForm;
